/**
 * Doors, windows and missing walls (F7, F8, F12) and the defaults flow (F13).
 *
 * Doors have two defaults (DD-SF.16): a door on a wall with a room across it (a
 * shared wall, or a door linked to a room beyond) takes the interior default; any
 * other wall is exterior and takes the exterior default. Width and type both.
 */
import { POLICY } from './policy';
import { EPS, clamp, clone, dist, edgeOf, findRoom, snapIn } from './geom';
import { ceilingLimitIn, validateMissing } from './missingKinds';
import { uid } from './plan';
import { alignBifold, doorWithDefaults, newDoorFields } from './doors';
import { shortWallFit } from './doorsFit';
import { adjacent, ivAt, partnerOf, sharedMap, syncLinked } from './shared';
import type {
  DoorDefault, DoorSide, DoorV2, Fail, MissingWallV2, Opening, OpeningKind, Plan, Room, SharedMap,
  WindowDefault, WindowV2,
} from './types';

const WINDOW_KEYS: (keyof WindowV2)[] = ['style', 'material', 'grid', 'glazing', 'impact', 'grade', 'trim'];

/** The window a new one starts as: the policy default overlaid with the plan's. */
export function windowDefaults(plan: Plan): WindowDefault {
  const p = POLICY.window_default;
  const base: WindowDefault = {
    widthFt: p.width_ft, heightFt: p.height_ft, style: p.style, material: p.material, grid: p.grid,
    glazing: p.glazing, impact: p.impact, grade: p.grade, trim: p.trim,
  };
  return { ...base, ...(plan.defaults.window ?? {}) };
}

/** The plan's door default for one side of the house. */
export function doorDefault(plan: Plan, side: DoorSide): DoorDefault {
  return side === 'interior' ? plan.defaults.doorInterior : plan.defaults.doorExterior;
}

/** Interior when wall `vid` of room `r` has a room across it at offset `s`. */
export function doorSideAt(plan: Plan, r: Room, vid: number, s: number, M?: SharedMap): DoorSide {
  return ivAt(M ?? sharedMap(plan), r.id, vid, s) ? 'interior' : 'exterior';
}

/** The side an existing door is on: across a shared wall, or linked to a room beyond. */
export function doorSideOf(plan: Plan, r: Room, o: Opening, M?: SharedMap): DoorSide {
  if (partnerOf(plan, r, o)) return 'interior';
  return doorSideAt(plan, r, o.edgeStart, o.offFt + o.widthFt / 2, M);
}

/** The room an opening belongs to (opening ids are plan-unique). */
function roomOfOpening(plan: Plan, o: Opening): Room | null {
  return plan.rooms.find((r) => r.openings.some((q) => q === o || q.id === o.id)) ?? null;
}

/** The door fields a door without any takes: the default type for its wall. */
function doorFields(plan: Plan, r: Room, o: Opening): DoorV2 {
  const side = doorSideOf(plan, r, o);
  return newDoorFields(plan, doorDefault(plan, side).type, side);
}

function defaultWidth(plan: Plan, kind: OpeningKind, side: DoorSide): number {
  if (kind === 'door') return doorDefault(plan, side).widthFt;
  if (kind === 'window') return windowDefaults(plan).widthFt;
  return POLICY.missing_wall_default.width_ft;
}

/**
 * A new opening of `kind` from the current defaults (no position yet). A door
 * needs its wall's side; `addOpening` resolves it from the wall the door lands
 * on. Without one a door is taken as exterior.
 */
export function newOpeningSpec(
  plan: Plan, kind: OpeningKind, side: DoorSide = 'exterior',
): Omit<Opening, 'id' | 'edgeStart' | 'offFt'> {
  const w = defaultWidth(plan, kind, side);
  if (kind === 'door') {
    return { kind, widthFt: w, door: newDoorFields(plan, doorDefault(plan, side).type, side) };
  }
  if (kind === 'window') {
    const d = windowDefaults(plan);
    const win = Object.fromEntries(WINDOW_KEYS.map((k) => [k, d[k]])) as unknown as WindowV2;
    return { kind, widthFt: w, heightFt: d.heightFt, window: win };
  }
  return { kind, widthFt: w, missing: { trim: POLICY.missing_wall_default.trim } };
}

/**
 * Add an opening centred at `off` on wall `vid`. On a shared wall the room
 * across gets the linked copy (F7, PRP case 10), from either side.
 */
export function addOpening(
  plan: Plan, rid: string, vid: number, off: number, kind: OpeningKind,
): { ok: true; oid: number; link?: number; partner?: { rid: string; oid: number } } | Fail<'missing' | 'too-short'> {
  const r = findRoom(plan, rid);
  const E = r && edgeOf(r, vid);
  if (!r || !E) return { ok: false, err: 'missing', message: 'That wall is gone' };
  const L = dist(E.a, E.b);
  // The door's default comes from the wall it lands on (DD-SF.16).
  const spec = newOpeningSpec(plan, kind, doorSideAt(plan, r, vid, clamp(off, 0, L)));
  const w = spec.widthFt;
  // H8/J13: a door or missing wall is never refused because the wall is short.
  const fit = kind === 'window' ? null : shortWallFit(L, kind, w);
  if (fit) return addOpeningAt(plan, r, vid, fit.offFt, { ...spec, widthFt: fit.widthFt });
  if (L < w + POLICY.opening_clearance_ft) return { ok: false, err: 'too-short', message: 'This wall is too short for that' };
  return addOpeningAt(plan, r, vid, snapIn(clamp(off - w / 2, 0, L - w)), spec);
}

/**
 * Add `spec` at offset `offFt` on wall `vid` of `r`, as given (no clamp or snap).
 * On a shared wall the room across gets the linked copy (F7).
 */
export function addOpeningAt(
  plan: Plan, r: Room, vid: number, offFt: number, spec: Omit<Opening, 'id' | 'edgeStart' | 'offFt'>,
): { ok: true; oid: number; link?: number; partner?: { rid: string; oid: number } } {
  const o: Opening = { ...clone(spec), id: uid(plan), edgeStart: vid, offFt };
  r.openings.push(o);
  const iv = ivAt(sharedMap(plan), r.id, vid, o.offFt + o.widthFt / 2);
  if (iv) {
    const q = findRoom(plan, iv.orid)!;
    o.link = uid(plan);
    const copy: Opening = { ...clone(o), id: uid(plan), edgeStart: iv.ovid, offFt: 0 };
    q.openings.push(copy);
    syncLinked(plan, r, o);
    return { ok: true, oid: o.id, link: o.link, partner: { rid: q.id, oid: copy.id } };
  }
  return { ok: true, oid: o.id };
}

export interface OpeningPatch {
  offFt?: number;
  widthFt?: number;
  heightFt?: number;
  door?: Partial<DoorV2>;
  window?: Partial<WindowV2>;
  missing?: Partial<MissingWallV2>;
}

/**
 * Edit an opening (the sheet's rows and chips). Validates like the prototype;
 * the adjacent linked copy follows (F7).
 */
export function updateOpening(
  plan: Plan, rid: string, oid: number, patch: OpeningPatch,
): { ok: true } | Fail<'missing' | 'width' | 'offset' | 'height' | 'overlap' | 'limit' | 'invalid'> {
  const r = findRoom(plan, rid);
  const o = r?.openings.find((q) => q.id === oid);
  const E = r && o ? edgeOf(r, o.edgeStart) : null;
  if (!r || !o) return { ok: false, err: 'missing', message: 'That opening is gone' };
  const L = E ? dist(E.a, E.b) : 0;
  const off = patch.offFt ?? o.offFt, w = patch.widthFt ?? o.widthFt;
  if (patch.widthFt != null && (w < POLICY.opening_min_width_ft || off + w > L + 1e-6)) {
    return { ok: false, err: 'width', message: 'That width does not fit on this wall' };
  }
  if (patch.offFt != null && off + w > L + 1e-6) return { ok: false, err: 'offset', message: 'That runs past the end of the wall' };
  if (patch.heightFt != null && patch.heightFt < POLICY.opening_min_height_ft) return { ok: false, err: 'height', message: 'Too short' };
  // E16: openings on one wall may touch end to end, never overlap.
  if ((patch.offFt != null || patch.widthFt != null) && overlapsOthers(r, o.edgeStart, off, off + w, o.id)) {
    return { ok: false, err: 'overlap', message: 'Openings on one wall can touch but not overlap' };
  }
  if (patch.missing && o.kind === 'missing') {
    const merged = { ...(o.missing ?? { trim: POLICY.missing_wall_default.trim }), ...patch.missing };
    if (merged.kind) {
      const v = validateMissing(merged, ceilingLimitIn(plan, r, o));
      if (!v.ok) return { ok: false, err: v.err, message: v.message };
    }
  }
  if (patch.offFt != null) o.offFt = patch.offFt;
  if (patch.widthFt != null) o.widthFt = patch.widthFt;
  if (patch.heightFt != null) o.heightFt = patch.heightFt;
  // B-7357: the Folds chip (a side) sets the swing; a swing alone sets the side.
  if (patch.door && o.kind === 'door') {
    const by = patch.door.bifoldSide == null && patch.door.out != null ? 'swing' : 'side';
    o.door = doorWithDefaults({ ...(o.door ?? doorFields(plan, r, o)), ...patch.door }, by);
  }
  if (patch.window && o.kind === 'window') o.window = { ...(o.window ?? (newOpeningSpec(plan, 'window').window as WindowV2)), ...patch.window };
  if (patch.missing && o.kind === 'missing') o.missing = { ...(o.missing ?? { trim: POLICY.missing_wall_default.trim }), ...patch.missing };
  syncLinked(plan, r, o);
  return { ok: true };
}

/** True when [s0, s1] on wall `vid` overlaps another opening there (touching is allowed, E16). */
export function overlapsOthers(r: Room, vid: number, s0: number, s1: number, exceptId?: number): boolean {
  return r.openings.some((q) => q.edgeStart === vid && q.id !== exceptId
    && s0 < q.offFt + q.widthFt - EPS && s1 > q.offFt + EPS);
}

/** Swing in/out button (F8 fallback). */
export function toggleSwing(plan: Plan, rid: string, oid: number): { ok: true } | Fail<'missing'> {
  const r = findRoom(plan, rid);
  const o = r?.openings.find((q) => q.id === oid);
  if (!r || !o || o.kind !== 'door') return { ok: false, err: 'missing', message: 'That door is gone' };
  o.door = { ...(o.door ?? doorFields(plan, r, o)) };
  o.door.out = !o.door.out;
  alignBifold(o.door, 'swing'); // B-7357: a bifold's side is its swing
  syncLinked(plan, r, o);
  return { ok: true };
}

/** Hinge side button (F8 fallback). */
export function toggleHinge(plan: Plan, rid: string, oid: number): { ok: true } | Fail<'missing'> {
  const r = findRoom(plan, rid);
  const o = r?.openings.find((q) => q.id === oid);
  if (!r || !o || o.kind !== 'door') return { ok: false, err: 'missing', message: 'That door is gone' };
  o.door = { ...(o.door ?? doorFields(plan, r, o)) };
  o.door.hingeAtEnd = !o.door.hingeAtEnd;
  syncLinked(plan, r, o);
  return { ok: true };
}

/** Delete an opening, and its partner only while the rooms are adjacent (F7). */
export function deleteOpening(plan: Plan, rid: string, oid: number): { ok: true; vid: number; partnerDeleted: boolean } | Fail<'missing'> {
  const r = findRoom(plan, rid);
  const o = r?.openings.find((q) => q.id === oid);
  if (!r || !o) return { ok: false, err: 'missing', message: 'That opening is gone' };
  const P = adjacent(plan, r, o);
  if (P) P.r.openings = P.r.openings.filter((q) => q !== P.o);
  r.openings = r.openings.filter((q) => q !== o);
  return { ok: true, vid: o.edgeStart, partnerDeleted: !!P };
}

/** Does this door or window differ from the current default? */
export function differsFromDefault(plan: Plan, o: Opening): boolean {
  if (o.kind === 'window') {
    const d = windowDefaults(plan);
    return d.widthFt !== o.widthFt || d.heightFt !== o.heightFt || WINDOW_KEYS.some((k) => d[k] !== o.window?.[k]);
  }
  if (o.kind === 'door') {
    const r = roomOfOpening(plan, o);
    const d = doorDefault(plan, r ? doorSideOf(plan, r, o) : 'exterior');
    return d.widthFt !== o.widthFt || d.type !== o.door?.type;
  }
  return false;
}

/**
 * F13: after Done on a door or window, ask "Use this for every new ...?" when
 * it was never asked for that kind and the opening is new or differs.
 */
export function shouldAskDefault(plan: Plan, o: Opening, isNew: boolean): boolean {
  if (o.kind !== 'window' && o.kind !== 'door') return false;
  const asked = o.kind === 'window' ? plan.defaults.windowAsked : plan.defaults.doorAsked;
  return !asked && (isNew || differsFromDefault(plan, o));
}

/**
 * "Make this my default" (and "Make default" in the prompt). A door sets the
 * interior or the exterior default, by the wall it sits on.
 */
export function makeDefault(plan: Plan, o: Opening): { ok: true } | Fail<'kind'> {
  if (o.kind === 'window') {
    const cur = windowDefaults(plan);
    const win: Partial<WindowV2> = o.window ?? {};
    const next: WindowDefault = { ...cur, widthFt: o.widthFt, heightFt: o.heightFt ?? cur.heightFt };
    WINDOW_KEYS.forEach((k) => { if (win[k] != null) next[k] = win[k] as string; });
    plan.defaults.window = next;
    plan.defaults.windowAsked = true;
    return { ok: true };
  }
  if (o.kind === 'door') {
    const r = roomOfOpening(plan, o);
    const side = r ? doorSideOf(plan, r, o) : 'exterior';
    const next = { type: o.door?.type ?? doorDefault(plan, side).type, widthFt: o.widthFt };
    if (side === 'interior') plan.defaults.doorInterior = next; else plan.defaults.doorExterior = next;
    plan.defaults.doorAsked = true;
    return { ok: true };
  }
  return { ok: false, err: 'kind', message: 'Missing walls have no default' };
}

/** "Not now" in the prompt. */
export function declineDefault(plan: Plan, kind: 'door' | 'window'): void {
  if (kind === 'door') plan.defaults.doorAsked = true; else plan.defaults.windowAsked = true;
}

/** Defaults sheet "Ask again next time". */
export function resetAskedDefaults(plan: Plan): void {
  plan.defaults.doorAsked = false;
  plan.defaults.windowAsked = false;
}

/** Defaults sheet: default ceiling for new rooms. */
export function setDefaultCeiling(plan: Plan, ft: number): { ok: true } | Fail<'too-low'> {
  if (ft < POLICY.ceiling_min_ft) return { ok: false, err: 'too-low', message: `At least ${POLICY.ceiling_min_ft} feet` };
  plan.defaults.ceilingFt = ft;
  return { ok: true };
}

